import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { scoreColor, fmtRM, STAGE_LABELS, STAGE_COLORS, TIER_COLORS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { Search, Plus, Flame, Users } from "lucide-react";

export default function CRM() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "", source: "Manual", tags: "" });
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts"], queryFn: () => api.get("/contacts").then((r) => r.data) });

  const filtered = contacts.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search) || (c.interest || "").toLowerCase().includes(search.toLowerCase()));

  const create = async () => {
    if (!form.name || !form.phone) { toast.error("Nama & telefon diperlukan"); return; }
    await api.post("/contacts", { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()) : [] });
    toast.success("Pelanggan ditambah");
    setOpen(false);
    setForm({ name: "", phone: "", email: "", interest: "", source: "Manual", tags: "" });
    qc.invalidateQueries({ queryKey: ["contacts"] });
  };

  return (
    <div className="p-6 space-y-5 max-w-7xl mx-auto" data-testid="crm-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="h-6 w-6 text-primary" /> CRM</h1>
          <p className="text-muted-foreground text-sm mt-1">{contacts.length} profil pelanggan. Setiap nombor WhatsApp jadi lead.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input data-testid="crm-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari pelanggan..." className="h-9 pl-9 pr-3 rounded-full bg-secondary text-sm outline-none w-56" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="crm-add-contact-button" className="rounded-full"><Plus className="h-4 w-4 mr-1.5" /> Pelanggan</Button>
            </DialogTrigger>
            <DialogContent data-testid="crm-add-dialog">
              <DialogHeader><DialogTitle>Tambah Pelanggan</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {[["name", "Nama"], ["phone", "Telefon (+60...)"], ["email", "Emel"], ["interest", "Minat / Produk"], ["source", "Sumber"], ["tags", "Tags (dipisah koma)"]].map(([k, l]) => (
                  <div key={k}>
                    <Label className="text-xs">{l}</Label>
                    <Input data-testid={`crm-input-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" />
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button onClick={create} data-testid="crm-save-contact-button" className="rounded-full w-full">Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Pelanggan</th>
                <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Minat</th>
                <th className="text-left font-semibold px-4 py-3">Score</th>
                <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Stage</th>
                <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Sumber</th>
                <th className="text-right font-semibold px-4 py-3">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`crm-row-${c.id}`} onClick={() => navigate(`/app/crm/${c.id}`)} className="border-t border-border hover:bg-secondary/40 transition-colors cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">{c.name[0]}</div>
                      <div>
                        <p className="font-semibold">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{c.interest}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${scoreColor(c.lead_score)} border-0 rounded-full`}>
                      {c.lead_score >= 85 && <Flame className="h-3 w-3 mr-1" />}{c.lead_score}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <Badge className={`${STAGE_COLORS[c.stage]} border-0 text-[10px] rounded`}>{STAGE_LABELS[c.stage]}</Badge>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">{c.source}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtRM(c.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
